import React, { useState } from 'react'
import {
  ArrowLeft,
  Calendar,
  Save,
  RotateCcw,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import sacMasterService from '../../services/sacMasterService'

const SACMasterForm = () => {
  const navigate = useNavigate()
  const [showHelp, setShowHelp] = useState(false)
  const [formData, setFormData] = useState({
    sacCode: '',
    description: '',
    gstRate: '0',
    igstRate: '0',
    cgstRate: '0',
    sgstRate: '0',
    utgstRate: '0',
    revisionNo: 'Rev 1',
    revisionDate: new Date().toISOString().split('T')[0]
  })

  const handleInputChange = (e) => {
    const { name, value, type } = e.target
    
    setFormData(prev => {
      const updatedData = {
        ...prev,
        [name]: type === 'number' ? (parseFloat(value) || 0) : value
      }
      
      // Auto-calculate tax rates when GST Rate changes
      if (name === 'gstRate' && type === 'number' && !isNaN(parseFloat(value))) {
        const gstRate = parseFloat(value) || 0
        updatedData.igstRate = gstRate
        updatedData.cgstRate = gstRate / 2
        updatedData.sgstRate = gstRate / 2
        updatedData.utgstRate = gstRate / 2
      }
      
      return updatedData
    })
  }

  const validateForm = () => {
    if (!formData.sacCode.trim()) {
      toast.error('SAC Code is required')
      return false
    }
    if (!/^\d{6}$/.test(formData.sacCode.trim())) {
      toast.error('SAC Code must be exactly 6 digits')
      return false
    }
    if (!formData.description.trim()) {
      toast.error('Description of Service is required')
      return false
    }
    if (parseFloat(formData.gstRate) < 0 || parseFloat(formData.gstRate) > 100) {
      toast.error('GST Rate must be between 0 and 100')
      return false
    }
    if (parseFloat(formData.igstRate) < 0 || parseFloat(formData.igstRate) > 100) {
      toast.error('IGST Rate must be between 0 and 100')
      return false
    }
    if (parseFloat(formData.sgstRate) < 0 || parseFloat(formData.sgstRate) > 100) {
      toast.error('SGST Rate must be between 0 and 100')
      return false
    }
    if (parseFloat(formData.cgstRate) < 0 || parseFloat(formData.cgstRate) > 100) {
      toast.error('CGST Rate must be between 0 and 100')
      return false
    }
    if (parseFloat(formData.utgstRate) < 0 || parseFloat(formData.utgstRate) > 100) {
      toast.error('UTGST Rate must be between 0 and 100')
      return false
    }
    if (!formData.revisionDate) {
      toast.error('Revision Date is required')
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    try {
      toast.info('Creating SAC Master record...')
      const response = await sacMasterService.createSAC({
        sacCode: formData.sacCode,
        description: formData.description,
        gstRate: parseFloat(formData.gstRate),
        igstRate: parseFloat(formData.igstRate),
        sgstRate: parseFloat(formData.sgstRate),
        cgstRate: parseFloat(formData.cgstRate),
        utgstRate: parseFloat(formData.utgstRate),
        revisionNo: formData.revisionNo,
        revisionDate: formData.revisionDate,
        status: 'Active'
      })

      if (response.success) {
        toast.success('SAC Master record created successfully!', { autoClose: 2000 })
        navigate('/sales/sac-master')
      } else {
        toast.error(response.message || 'Failed to create SAC Master record')
      }
    } catch (error) {
      console.error('Error creating SAC Master record:', error)
      if (error.response?.status === 400 && error.response.data?.message.includes('SAC code already exists')) {
        toast.error('SAC Code already exists. Please use a different code.')
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to perform this action.')
      } else {
        toast.error('Failed to create SAC Master record. Please try again.')
      }
    }
  }

  const handleReset = () => {
    setFormData({
      sacCode: '',
      description: '',
      gstRate: '0',
      igstRate: '0',
      cgstRate: '0',
      sgstRate: '0',
      utgstRate: '0',
      revisionNo: 'Rev 1',
      revisionDate: new Date().toISOString().split('T')[0]
    })
    toast.info('Form reset to default values')
  }

  const handleBack = () => {
    navigate('/sales/sac-master')
  }

  const breadcrumbItems = [
    { label: 'Sales', path: '/sales', onClick: () => navigate('/sales') },
    { label: 'Masters', path: '/sales?tab=masters', onClick: () => navigate('/sales?tab=masters') },
    { label: 'SAC Master', path: '/sales/sac-master', onClick: () => navigate('/sales/sac-master') },
    { label: 'Create', path: '/sales/sac-master/form', isCurrent: true }
  ]

  const helpContent = (
    <>
      <h3>What is SAC Master?</h3>
      <p>SAC Master lets you <strong>manage Services Accounting Code (SAC) master data</strong> for GST—6-digit code, description, GST/IGST/CGST/SGST/UTGST rates, and revision details.</p>
      <h3>How to use</h3>
      <p>Enter <strong>SAC Code</strong> (6 digits), <strong>Description</strong>, and <strong>GST Rate</strong>. Other tax rates can auto-fill from GST rate. Set revision no. and date. Use <strong>Save</strong> to create. Use <strong>Reset</strong> to clear or navigate back to cancel.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>SAC Master</h1>
          <p>Manage Services Accounting Code master data</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>SAC Master</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header"><h3>SAC Details</h3></div>
          {/* Row 1: SAC Code, Description, GST Rate */}
          <div className="grid-row-3">
            <div className="form-group">
              <label htmlFor="sacCode" className="form-label required">
                SAC Code
              </label>
              <input
                type="text"
                id="sacCode"
                name="sacCode"
                value={formData.sacCode}
                onChange={handleInputChange}
                onKeyPress={(e) => {
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault()
                  }
                }}
                className="form-input"
                placeholder="Enter 6-digit SAC Code"
                maxLength="6"
                pattern="[0-9]{6}"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description" className="form-label required">
                Description of Service
              </label>
              <input
                type="text"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter service description"
              />
            </div>

            <div className="form-group">
              <label htmlFor="gstRate" className="form-label required">
                GST Rate %
              </label>
              <input
                type="number"
                id="gstRate"
                name="gstRate"
                value={formData.gstRate}
                onChange={handleInputChange}
                className="form-input"
                step="0.01"
                min="0"
              />
            </div>
          </div>

          <div className="section-header"><h3>Tax Breakdown</h3></div>
          {/* Row 2: IGST Rate, CGST Rate, SGST Rate */}
          <div className="grid-row-3">
            <div className="form-group">
              <label htmlFor="igstRate" className="form-label required">
                IGST Rate %
              </label>
              <input
                type="number"
                id="igstRate"
                name="igstRate"
                value={formData.igstRate}
                onChange={handleInputChange}
                className="form-input"
                step="0.01"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="cgstRate" className="form-label required">
                CGST Rate %
              </label>
              <input
                type="number"
                id="cgstRate"
                name="cgstRate"
                value={formData.cgstRate}
                onChange={handleInputChange}
                className="form-input"
                step="0.01"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="sgstRate" className="form-label required">
                SGST Rate %
              </label>
              <input
                type="number"
                id="sgstRate"
                name="sgstRate"
                value={formData.sgstRate}
                onChange={handleInputChange}
                className="form-input"
                step="0.01"
                min="0"
              />
            </div>
          </div>

          <div className="section-header"><h3>Revision</h3></div>
          {/* Row 3: UTGST Rate, Revision No, Revision Date */}
          <div className="grid-row-3">
            <div className="form-group">
              <label htmlFor="utgstRate" className="form-label required">
                UTGST Rate %
              </label>
              <input
                type="number"
                id="utgstRate"
                name="utgstRate"
                value={formData.utgstRate}
                onChange={handleInputChange}
                className="form-input"
                step="0.01"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="revisionNo" className="form-label">
                Current Version
              </label>
              <input
                type="text"
                id="revisionNo"
                name="revisionNo"
                value="Rev 1"
                className="form-input"
                disabled
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="revisionDate" className="form-label required">
                Revision Date
              </label>
              <div className="date-input-wrapper">
                <input
                  type="text"
                  id="revisionDate"
                  name="revisionDate"
                  value={formData.revisionDate}
                  onChange={handleInputChange}
                  className="form-input date-input"
                  placeholder="DD-MM-YYYY"
                />
                <Calendar size={16} className="calendar-icon" />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn-primary"
            >
              <Save size={16} />
              Save
            </button>
          </div>
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="SAC Master – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default SACMasterForm
